import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { BookOpen, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    school_name: '',
    circuit: '',
    cellphone: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/teacher/profile`);
      setProfile(response.data);
      setFormData({
        name: response.data.name || '',
        surname: response.data.surname || '',
        school_name: response.data.school_name || '',
        circuit: response.data.circuit || '',
        cellphone: response.data.cellphone || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/teacher/profile`, formData);
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-sky-50">Loading...</div>;

  const canEdit = !profile?.next_profile_edit || new Date(profile.next_profile_edit) < new Date();

  return (
    <div className="min-h-screen bg-sky-50" data-testid="profile-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-4xl mx-auto">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card p-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">My Profile</h2>
          
          {!canEdit && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="edit-restriction-notice">
              <p className="text-sm text-amber-800">
                You can only update your profile once per month. Next update available on: {new Date(profile.next_profile_edit).toLocaleDateString()}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                  required
                  disabled={!canEdit}
                  data-testid="name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Surname</label>
                <input
                  type="text"
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                  required
                  disabled={!canEdit}
                  data-testid="surname-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">School Name</label>
              <input
                type="text"
                value={formData.school_name}
                onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                required
                disabled={!canEdit}
                data-testid="school-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Circuit/District</label>
              <input
                type="text"
                value={formData.circuit}
                onChange={(e) => setFormData({ ...formData, circuit: e.target.value })}
                className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                required
                disabled={!canEdit}
                data-testid="circuit-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Cellphone Number</label>
              <input
                type="tel"
                value={formData.cellphone}
                onChange={(e) => setFormData({ ...formData, cellphone: e.target.value })}
                className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                required
                disabled={!canEdit}
                data-testid="cellphone-input"
              />
            </div>

            {canEdit && (
              <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6" data-testid="save-profile-button">
                Save Profile
              </Button>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}